import { useEffect, useRef, useState } from "react";
import { type Account, ApiError, type Location, type Product, api, formatMoney } from "./api";
import { singaporeToday } from "./dates";
import { SelectField } from "./fields";
import { shrink, type Taken, turned, usePastedImages } from "./pos/photos";
import { productName } from "./productName";
import { Alert, Field, Modal, Working } from "./ui";

/**
 * A sale keyed in after its day (D, 10 October 2026): from a PayNow credit on the
 * statement that has no sale behind it, or from the photos taken at the table before the
 * system was in use. Drop the photos in and the day is taken from them; pick the items;
 * from a bank line, the amount must come to the line's and the line is matched on saving.
 */
export function EarlierSale({
  start,
  onClose,
  onDone,
}: {
  readonly start?: {
    readonly day: string;
    readonly amountCents: number;
    readonly accountId: string;
    readonly bankLineId: string;
    readonly payer: string | null;
  };
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [day, setDay] = useState(start?.day ?? "");
  const [time, setTime] = useState("");
  const [dayFrom, setDayFrom] = useState<string | undefined>();
  const [method, setMethod] = useState<"cash" | "paynow">(start ? "paynow" : "paynow");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState(start?.accountId ?? "");
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [lines, setLines] = useState<{ key: number; productId: string; note: string; quantity: string; total: string }[]>([
    { key: 1, productId: "", note: "", quantity: "1", total: start ? (start.amountCents / 100).toFixed(2) : "" },
  ]);
  const [customer, setCustomer] = useState(start?.payer ?? "");
  const [photos, setPhotos] = useState<Taken[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api.accounts().then((result) => setAccounts(result.accounts.filter((one) => one.isActive && one.ownerKind === "company")));
    void api.locations().then((result) => setLocations(result.locations.filter((one) => one.isActive)));
    void api.products().then((result) => setProducts(result.products.filter((one) => one.isActive)));
  }, []);
  const choices = accounts.filter((one) => one.kind === (method === "cash" ? "cash" : "bank"));
  useEffect(() => {
    if (!choices.some((one) => one.id === accountId)) setAccountId(choices[0]?.id ?? "");
  }, [method, accounts.length]);

  const counts = (productId: string): boolean => products.find((one) => one.id === productId)?.tracksStock ?? false;
  const cents = (text: string): number => Math.round(Number(text.replace(/[^\d.]/g, "")) * 100) || 0;
  const total = lines.reduce((sum, line) => sum + cents(line.total), 0);
  const off = start ? start.amountCents - total : 0;

  async function add(files: FileList | File[] | null): Promise<void> {
    const list = [...(files ?? [])].filter((file) => file.type.startsWith("image/")).slice(0, 12);
    if (list.length === 0) return;
    // The first photo says when it was: its own record of when it was taken, or failing
    // that when the file was made. Not over a bank line's day, which the bank already says.
    if (!start && list[0]) {
      const when = await photoTime(list[0]);
      if (when) {
        setDay(when.day);
        setTime(when.time);
        setDayFrom(when.from);
      }
    }
    for (const file of list) {
      try {
        const taken = await shrink(file);
        setPhotos((current) => [...current, taken]);
      } catch {
        setProblem("A photo could not be read");
      }
    }
  }

  usePastedImages((files) => void add(files));

  async function save(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      const made = await api.recordEarlierSale({
        day,
        time: time || null,
        lines: lines
          .filter((line) => line.productId)
          .map((line) => {
            const counted = counts(line.productId);
            const quantity = Math.max(1, Number(line.quantity) || 1);
            return {
              productId: line.productId,
              // A counted product is so many at a price each; anything else is one line at its amount, as on the iPad.
              quantity: counted ? quantity : 1,
              unitPriceCents: counted ? Math.round(cents(line.total) / quantity) : cents(line.total),
              note: counted ? null : [line.note.trim(), quantity > 1 ? `${quantity} pcs` : ""].filter(Boolean).join(", ") || null,
            };
          }),
        method,
        accountId,
        locationId: locationId || null,
        customerName: customer.trim() || null,
        bankLineId: start?.bankLineId ?? null,
      });
      let failed = 0;
      for (const photo of photos) {
        await api.addSalePhoto(made.saleId, { contentType: photo.contentType, data: photo.data }).catch(() => (failed += 1));
      }
      for (const photo of photos) URL.revokeObjectURL(photo.preview);
      onDone(
        `${made.reference} recorded on ${day}${start ? " and matched to this payment" : ""}.` +
          (failed ? ` ${failed} ${failed === 1 ? "photo" : "photos"} could not be added: drop them onto the sale on Sales and buybacks.` : ""),
      );
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The sale could not be saved");
      setBusy(false);
    }
  }

  const ready = day && accountId && total > 0 && (!start || off === 0) && lines.every((line) => line.productId);

  return (
    <Modal
      open
      size="lg"
      title={start ? "Record the sale for this payment" : "Record an earlier sale"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy || !ready} onClick={() => void save()}>
            {busy ? <Working label="Saving" /> : `Record sale of ${formatMoney(total)}`}
          </button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}

      <div
        className={`rounded-md border-2 border-dashed p-4 mb-4 text-center cursor-pointer ${dragging ? "border-primary bg-primary/10" : "border-defaultborder"}`}
        onClick={() => picker.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void add(event.dataTransfer.files);
        }}
      >
        <i className="ri-download-2-line text-[1.5rem] text-[#8c9097]" />
        <div className="text-[0.813rem]">
          <span className="font-semibold">Drop the photos here</span>, paste a copied image, or choose them: the items, the transfer screenshot.
          {start ? "" : " The day is taken from the first photo."}
        </div>
        <input ref={picker} type="file" accept="image/*" multiple className="hidden" onChange={(event) => void add(event.target.files)} />
        {photos.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-2 mt-3" onClick={(event) => event.stopPropagation()}>
            {photos.map((photo) => (
              <div key={photo.id} className="w-16 h-16 rounded-md overflow-hidden border border-defaultborder relative">
                <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  aria-label="Take this photo off"
                  className="absolute top-0 end-0 bg-danger text-white text-[0.625rem] px-1 rounded-bl"
                  onClick={() => setPhotos((current) => current.filter((one) => one.id !== photo.id))}
                >
                  <i className="ri-close-line" />
                </button>
                <button
                  type="button"
                  aria-label="Turn this photo"
                  title="Turn the photo"
                  className="absolute bottom-0 start-0 bg-dark/70 text-white text-[0.75rem] w-6 h-6 rounded-tr flex items-center justify-center"
                  onClick={(event) => {
                    event.stopPropagation();
                    void turned(photo).then((done) => setPhotos((current) => current.map((one) => (one.id === photo.id ? done : one))));
                  }}
                >
                  <i className="ri-clockwise-line" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Field
          id="earlier-day"
          label="Day sold"
          className="sm:col-span-4 col-span-6"
          hint={start ? "The day the payment came in." : dayFrom ? `From the photo: ${dayFrom}.` : undefined}
        >
          <input id="earlier-day" type="date" className="form-control" value={day} max={singaporeToday()} onChange={(event) => setDay(event.target.value)} />
        </Field>
        <Field id="earlier-time" label="About what time" className="sm:col-span-2 col-span-6" hint="Optional">
          <input id="earlier-time" type="time" className="form-control" value={time} onChange={(event) => setTime(event.target.value)} />
        </Field>
        <Field id="earlier-method" label="Paid by" className="sm:col-span-3 col-span-6">
          <SelectField<"cash" | "paynow">
            id="earlier-method"
            value={method}
            options={[
              { value: "paynow", label: "PayNow" },
              { value: "cash", label: "Cash" },
            ]}
            onChange={setMethod}
          />
        </Field>
        <Field id="earlier-account" label="Into" className="sm:col-span-3 col-span-6">
          <SelectField id="earlier-account" value={accountId} placeholder="Select" options={choices.map((one) => ({ value: one.id, label: one.name }))} onChange={setAccountId} />
        </Field>
        <Field id="earlier-location" label="Where (optional)" className="sm:col-span-6 col-span-12">
          <SelectField id="earlier-location" value={locationId} placeholder="Select" options={locations.map((one) => ({ value: one.id, label: one.name }))} onChange={setLocationId} />
        </Field>
        <Field id="earlier-customer" label="Customer (optional)" className="sm:col-span-6 col-span-12">
          <input id="earlier-customer" type="text" className="form-control" value={customer} onChange={(event) => setCustomer(event.target.value)} />
        </Field>
      </div>

      <p className="form-label mt-4 mb-2">What was sold</p>
      {lines.map((line) => (
        <div key={line.key} className="grid grid-cols-12 gap-2 mb-2 items-start">
          <div className="sm:col-span-6 col-span-12">
            <SelectField
              id={`earlier-product-${line.key}`}
              value={line.productId}
              placeholder="Product: Singles, Others or a product on the list"
              options={products.map((one) => ({ value: one.id, label: productName(one) }))}
              onChange={(productId) => setLines((current) => current.map((one) => (one.key === line.key ? { ...one, productId } : one)))}
            />
            {line.productId && !counts(line.productId) ? (
              <input
                type="text"
                className="form-control mt-1"
                value={line.note}
                placeholder="What it was, e.g. Singles (3 cards)"
                aria-label="What it was"
                onChange={(event) => setLines((current) => current.map((one) => (one.key === line.key ? { ...one, note: event.target.value } : one)))}
              />
            ) : null}
          </div>
          <input
            type="text"
            inputMode="numeric"
            className="form-control sm:col-span-2 col-span-4"
            aria-label="Quantity"
            value={line.quantity}
            onChange={(event) => setLines((current) => current.map((one) => (one.key === line.key ? { ...one, quantity: event.target.value } : one)))}
          />
          <input
            type="text"
            inputMode="decimal"
            className="form-control sm:col-span-3 col-span-6"
            aria-label="Line total"
            placeholder="Line total"
            value={line.total}
            onChange={(event) => setLines((current) => current.map((one) => (one.key === line.key ? { ...one, total: event.target.value } : one)))}
          />
          <button
            type="button"
            aria-label="Take this line off"
            className="ti-btn ti-btn-light !mb-0 sm:col-span-1 col-span-2"
            disabled={lines.length === 1}
            onClick={() => setLines((current) => current.filter((one) => one.key !== line.key))}
          >
            <i className="ri-delete-bin-line" />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0"
        onClick={() => setLines((current) => [...current, { key: Date.now(), productId: "", note: "", quantity: "1", total: start && off > 0 ? (off / 100).toFixed(2) : "" }])}
      >
        <i className="ri-add-line" /> Add another item
      </button>
      {start ? (
        <p className={`text-[0.813rem] mt-3 mb-0 ${off === 0 ? "text-success" : "text-danger"}`}>
          {off === 0
            ? `Comes to ${formatMoney(start.amountCents)}, the payment's amount.`
            : `The items come to ${formatMoney(total)}; the payment is ${formatMoney(start.amountCents)}. ${off > 0 ? `${formatMoney(off)} still to put on an item.` : `${formatMoney(-off)} too much.`}`}
        </p>
      ) : null}
    </Modal>
  );
}

/**
 * When a photo was taken: the camera's own date inside a JPEG, or, for a screenshot or a
 * photo without one, when the file was made. Singapore time.
 */
async function photoTime(file: File): Promise<{ day: string; time: string; from: string } | undefined> {
  try {
    const head = new Uint8Array(await file.slice(0, 256 * 1024).arrayBuffer());
    let text = "";
    for (const byte of head) text += String.fromCharCode(byte);
    const found = /(20\d\d):(\d\d):(\d\d) (\d\d):(\d\d):\d\d/.exec(text);
    if (found) {
      const [, year, month, date, hour, minute] = found;
      return { day: `${year}-${month}-${date}`, time: `${hour}:${minute}`, from: "when it was taken" };
    }
  } catch {
    // Not a photo with a date inside; the file's own date is next.
  }
  if (!file.lastModified) return undefined;
  const at = new Date(file.lastModified);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const part = (type: string): string => parts.find((one) => one.type === type)?.value ?? "";
  return { day: `${part("year")}-${part("month")}-${part("day")}`, time: `${part("hour")}:${part("minute")}`, from: "when the file was saved" };
}
